import React from "react";

import type {
  State,
  PublicHoliday,
  AllHolidayOutput,
  MergedHoliday,
  AllMergedOutput,
} from "../types";

import {
  Container,
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";

import { ChevronLeft, ChevronRight, Today } from "@mui/icons-material";
import { useHolidays } from "../contexts/HolidayContext";
import { Calendar } from "../components/Calendar";
import { StateColorKey } from "../components/StateColorKey";
import { HolidayPillKey } from "../components/HolidayPillKey";
import { NextHolidayCountdown } from "../components/NextHolidayCountdown";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const HomePage: React.FC = () => {
  const { holidays, loading, error, currentYear, currentMonth, setCurrentMonth, loadYear } =
    useHolidays();

  const handlePreviousMonth = () => {
    if (currentMonth === 1) {
      loadYear(currentYear - 1);
      setCurrentMonth(12);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      loadYear(currentYear + 1);
      setCurrentMonth(1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleBackToToday = () => {
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth() + 1;

    if (todayYear !== currentYear) {
      loadYear(todayYear);
    }
    setCurrentMonth(todayMonth);
  };

  const isCurrentMonth = () => {
    const today = new Date();
    return today.getFullYear() === currentYear && today.getMonth() + 1 === currentMonth;
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 4 }}>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {/* Countdown */}
        <NextHolidayCountdown />

        {/* Month navigation */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
            gap: { xs: 1, sm: 2 },
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
              order: { xs: 1, md: 2 },
            }}
          >
            <IconButton
              onClick={handlePreviousMonth}
              size="medium"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                color: "text.secondary",
                "&:hover": {
                  borderColor: "text.primary",
                  bgcolor: "action.hover",
                },
              }}
            >
              <ChevronLeft />
            </IconButton>

            <Typography
              variant="h4"
              sx={{
                minWidth: { xs: "auto", sm: 200, md: 280 },
                textAlign: "center",
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
              }}
            >
              {MONTH_NAMES[currentMonth - 1]} {currentYear}
            </Typography>

            <IconButton
              onClick={handleNextMonth}
              size="medium"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                color: "text.secondary",
                "&:hover": {
                  borderColor: "text.primary",
                  bgcolor: "action.hover",
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: { xs: "100%", md: "auto" },
              order: { xs: 2, md: 3 },
            }}
          >
            {!isCurrentMonth() && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Today />}
                onClick={handleBackToToday}
                sx={{
                  fontWeight: 400,
                  fontSize: "0.875rem",
                  textTransform: "none",
                  color: "text.secondary",
                  borderColor: "divider",
                  "&:hover": {
                    borderColor: "text.secondary",
                    bgcolor: "action.hover",
                  },
                }}
              >
                Today
              </Button>
            )}
          </Box>
        </Box>

        {/* Loading state */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No data for {currentYear}
          </Alert>
        )}

        {/* Calendar */}
        {!loading && !error && (
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: 3,
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 380px)",
            }}
          >
            {/* Future year warning */}
            {currentYear >= new Date().getFullYear() + 1 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Data for {currentYear} may be incomplete. Future holiday dates are subject to
                change.
              </Alert>
            )}
            <Calendar year={currentYear} month={currentMonth} holidays={holidays} />
          </Paper>
        )}

        {/* State Color Key */}
        {!loading && !error && (
          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
            <StateColorKey />
          </Box>
        )}

        {/* Holiday Pill Key */}
        {!loading && !error && (
          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
            <HolidayPillKey />
          </Box>
        )}

       
      </Container>
    </Box>
  );
};
